import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Chip,
  Paper,
} from "@mui/material";
import { ChevronLeft, ChevronRight, PersonOutline } from "@mui/icons-material";
import { format, isSameDay, isSameMonth, parseISO } from "date-fns";

const normalizeDate = (d) => {
  if (!d) return new Date();
  if (d instanceof Date) return new Date(d);
  if (typeof d === "string") return parseISO(d);
  return new Date();
};

const getStatusForDay = (animal, day) => {
  const targetDate = normalizeDate(day);
  const record = animal.records.find((r) =>
    isSameDay(normalizeDate(r.date), targetDate)
  );
  return record ? record.status : null;
};

function StatusChip({ status }) {
  if (status === "Present") {
    return <Chip label="Present" color="success" size="small" sx={{ minHeight: 36 }} />;
  }
  if (status === "Absent") {
    return <Chip label="Absent" color="error" size="small" sx={{ minHeight: 36 }} />;
  }
  return (
    <Chip label="Unchecked" variant="outlined" size="small" sx={{ minHeight: 36 }} />
  );
}

export default function AttendanceDayList({
  animals,
  focusDay,
  monthAnchor,
  onFocusDayChange,
  onStatusClick,
  onAnimalClick,
}) {
  const canPrev = !isSameDay(
    focusDay,
    new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1)
  );
  const lastOfMonth = new Date(
    monthAnchor.getFullYear(),
    monthAnchor.getMonth() + 1,
    0
  );
  const canNext = !isSameDay(focusDay, lastOfMonth);

  const goPrev = () => {
    if (!canPrev) return;
    const d = new Date(focusDay);
    d.setDate(d.getDate() - 1);
    if (!isSameMonth(d, monthAnchor)) return;
    onFocusDayChange(d);
  };

  const goNext = () => {
    if (!canNext) return;
    const d = new Date(focusDay);
    d.setDate(d.getDate() + 1);
    if (!isSameMonth(d, monthAnchor)) return;
    onFocusDayChange(d);
  };

  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <IconButton
          aria-label="Previous day"
          onClick={goPrev}
          disabled={!canPrev}
          size="large"
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="subtitle1" align="center" sx={{ flex: 1, px: 1 }}>
          Roll call — {format(focusDay, "EEE, MMM d")}
        </Typography>
        <IconButton
          aria-label="Next day"
          onClick={goNext}
          disabled={!canNext}
          size="large"
        >
          <ChevronRight />
        </IconButton>
      </Box>
      <List disablePadding sx={{ maxHeight: "60vh", overflow: "auto" }}>
        {animals.map((animal) => {
          const status = getStatusForDay(animal, focusDay);
          return (
            <ListItemButton
              key={animal.id}
              onClick={() => onStatusClick(animal.id, focusDay, status)}
              sx={{
                alignItems: "flex-start",
                py: 1.5,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <ListItemText
                primaryTypographyProps={{ variant: "body1", fontWeight: 600 }}
                secondaryTypographyProps={{ variant: "body2" }}
                primary={animal.tag_number}
                secondary={animal.alias || "No alias"}
              />
              <Box sx={{ ml: 1, flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                <StatusChip status={status} />
                <IconButton
                  size="small"
                  edge="end"
                  aria-label="View animal profile"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAnimalClick(animal.id);
                  }}
                >
                  <PersonOutline />
                </IconButton>
              </Box>
            </ListItemButton>
          );
        })}
      </List>
      {animals.length === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary" align="center">
            No animals match your search.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
